import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  item: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
  },
  itemIncomplete: {
    borderColor: theme.palette.error.main,
    backgroundColor: 'rgba(211, 47, 47, 0.04)',
  },
  header: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  warningMark: {
    color: theme.palette.error.main,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1,
    flexShrink: 0,
  },
  meta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  metaError: {
    color: theme.palette.error.main,
  },
  details: {
    marginTop: theme.spacing(1.5),
  },
  removeButton: {
    minWidth: 32,
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  expandHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
}));

const EntitlementAccordionList = ({ items, getTitle, getMeta, renderDetails, onRemove, disabled, isIncomplete }) => {
  const classes = useStyles();
  const [expandedId, setExpandedId] = useState(null);

  return (
    <Box className={classes.root}>
      {items.map(item => {
        const expanded = expandedId === item.id;
        const incomplete = Boolean(isIncomplete?.(item));
        return (
          <Box key={item.id} className={`${classes.item}${incomplete ? ` ${classes.itemIncomplete}` : ''}`}>
            <Box
              className={classes.header}
              onClick={() => setExpandedId(expanded ? null : item.id)}
              role="button"
              tabIndex={0}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setExpandedId(expanded ? null : item.id);
                }
              }}
            >
              <Box className={classes.summaryContent}>
                <Box className={classes.titleRow}>
                  {incomplete ? (
                    <Typography className={classes.warningMark} component="span" aria-label="warning">
                      !
                    </Typography>
                  ) : null}
                  <Typography variant="subtitle2">{getTitle(item)}</Typography>
                </Box>
                <Typography className={`${classes.meta}${incomplete ? ` ${classes.metaError}` : ''}`}>
                  {getMeta(item)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography className={classes.expandHint}>{expanded ? '▲' : '▼'}</Typography>
                {onRemove ? (
                  <Button
                    className={classes.removeButton}
                    size="small"
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      onRemove(item.id);
                    }}
                  >
                    ×
                  </Button>
                ) : null}
              </Box>
            </Box>
            <Collapse in={expanded}>
              <Box className={classes.details}>{renderDetails(item)}</Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

EntitlementAccordionList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  getTitle: PropTypes.func.isRequired,
  getMeta: PropTypes.func.isRequired,
  renderDetails: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  isIncomplete: PropTypes.func,
};

EntitlementAccordionList.defaultProps = {
  onRemove: null,
  disabled: false,
  isIncomplete: null,
};

export default EntitlementAccordionList;
